import { PAGE_STATE } from '../../../tornado/utils/virtualization';

/**
 * Calculates vertical offsets of the items container, one per item, keeping a focused item
 * vertically centered while the list edges are kept within the panel paddings
 *
 * @param {Object} options all the options
 * @param {number} options.itemsCount amount of the items placed within the panel
 * @param {number} options.itemHeight height of a single item
 * @param {number} options.contentGap vertical gap between the items
 * @param {number} options.panelHeight height of the panel viewport
 * @param {number} options.paddingTop top padding of the panel
 * @param {number} options.paddingBottom bottom padding of the panel
 * @returns {number[]} container offsets, one per item
 */
export const getContainerPositions = ({
  itemsCount,
  itemHeight,
  contentGap,
  panelHeight,
  paddingTop,
  paddingBottom,
}) => {
  const positions = [];
  const halfOfPanelHeight = Math.round(panelHeight / 2);
  const halfOfItemHeight = Math.round(itemHeight / 2);
  const topOffset = paddingTop;
  const bottomOffset = panelHeight - itemsCount * itemHeight - Math.max(itemsCount - 1, 0) * contentGap - paddingBottom;

  for (let index = 0; index < itemsCount; index += 1) {
    const offset = index * (itemHeight + contentGap);

    positions.push(Math.min(Math.max(halfOfPanelHeight - offset - halfOfItemHeight, bottomOffset), topOffset));
  }

  return positions;
};

/**
 * Collects zero-based indexes of the pages which are not loaded yet, but hold the items
 * placed close enough to the focused one to be displayed soon
 *
 * @param {Object} options all the options
 * @param {number} options.index index of the focused item
 * @param {number} options.lookahead amount of the items ahead of the focused one to be covered by the loaded pages
 * @param {number} options.itemsPerPage amount of the items within a single page
 * @param {number} options.totalItems amount of the items within the whole block
 * @param {Object} options.pages block pages state, as provided by the layout
 * @returns {number[]} zero-based page indexes to request, in ascending order
 */
export const getPagesToRequest = ({ index, lookahead, itemsPerPage, totalItems, pages = {} }) => {
  if (!itemsPerPage || !totalItems) {
    return [];
  }

  const lastItemIndex = Math.min(index + lookahead, totalItems - 1);
  const lastPage = Math.floor(lastItemIndex / itemsPerPage);
  const result = [];

  for (let page = 0; page <= lastPage; page += 1) {
    if (pages[page] === PAGE_STATE.MISSING) {
      result.push(page);
    }
  }

  return result;
};
